import { useState } from "react";
import "./style.css";
import { useUserInfo } from "../../../../context/user";
import DeleteFileModal from "../../../../components/DeleteFileModal";
import Uploadfile from "../../../../components/uploadFiles";
import AddWebsite from "../../../../components/addwebsites";
import AssistantMemory from "../../../../components/memory";
import Plans from "../../../../components/Plans";
import { Link } from "react-router-dom";
import { useUsersContext } from "../../../../context/usersContext";
import { format } from "date-fns";
import { Input } from "components/ui/input";
import { Button } from "components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "components/ui/tooltip";
import { Search, FileText, Trash2 } from "lucide-react";
import { DataGrid, DataGridHeader, DataGridRow, DataGridCell } from "components/ui/data-grid";
import FilterDropdown from "../Files/component/FilterDropdown";
import { useDataInterpreterFiles, useDeleteDataInterpreterFiles } from "../../../../queries/knowledgeBase";

function DataInterpreter({ setActiveTab }) {
  const userInfo = useUserInfo()[0];
  const userActiveLocation = useUserInfo()[4];
  const profileId = userActiveLocation?.id;
  const [seletedFile, setSeletedFile] = useState();
  const [memory, setMemory] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredFiles, setFilteredFiles] = useState([]);
  const [agentFilter, setAgentFilter] = useState("");
  const [fileTypeFilter, setFileTypeFilter] = useState("");
  const [selectPlan, setSelectPlan] = useState();
  const [selectedItems, setSelectedItems] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const filesQuery = useDataInterpreterFiles(profileId, currentPage);
  const userFiles = filesQuery.data?.files;
  const pagination = filesQuery.data?.pagination || {};
  const deleteFilesMutation = useDeleteDataInterpreterFiles(profileId);
  const fileLoading = filesQuery.isFetching || deleteFilesMutation.isPending;

  const confirmDelete = (fileInfo) => {
    setSeletedFile(fileInfo);
  };

  const deleteFile = async (fileIdsToDelete) => {
    const idsToDelete = seletedFile ? [seletedFile?.id] : fileIdsToDelete;
    const agentIds = seletedFile && [seletedFile?.assistant_id];

    try {
      await deleteFilesMutation.mutateAsync({ ids: idsToDelete, assistantIds: agentIds });
      setSeletedFile();
      setSelectedItems([]);
    } catch (error) {
      alert(error.response?.data?.err || "An error occurred during deletion.");
    }
  };

  const filteredData = Array.isArray(userFiles)
    ? userFiles.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    : [];

  const totalPages = pagination.total || 0
  const maxButtonsToShow = 5;

  const applyFilters = (list) =>
    (list || []).filter((item) => {
      if (agentFilter && item.assistant_name !== agentFilter) return false;
      if (fileTypeFilter && item.name !== fileTypeFilter) return false;
      return true;
    });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = applyFilters(searchQuery ? filteredFiles : filteredData)

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const startPage = Math.max(currentPage - Math.floor(maxButtonsToShow / 2), 1);
  const endPage = Math.min(startPage + maxButtonsToShow - 1, totalPages);

  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);

    const filtered = userFiles.filter((file) => {
      const fileName = file.name ? file.name.toLowerCase() : "";
      const fileUsername = file.username ? file.username.toLowerCase() : "";
      return fileName.includes(query) || fileUsername.includes(query);
    });

    setFilteredFiles(filtered);
    setCurrentPage(1);
  };

  return (
    <div className="container-alignment">
      <div className="page-header [&_.page-display]:text-3xl [&_.page-display]:md:text-4xl [&_.page-lede]:text-sm pb-4">
        <span className="page-eyebrow">Configure</span>
        <h1 className="page-display">
          Data Interpreter. <span className="accent block md:inline">Spreadsheets your AI can reason over.</span>
        </h1>
        <p className="page-lede">
          Add example spreadsheets or CSVs your AI Agents should analyse. Ideal for
          reporting, audits, or structured data tasks. For writing tasks or business knowledge,{" "}
          <span
            className="cursor-pointer underline text-primary"
            onClick={() => setActiveTab("Knowledge Base")}
          >
            use the Knowledge Base
          </span>{" "}
          →
        </p>
      </div>

      <>
        <div className="first_box relative">
          <div className="relative w-full max-w-[740px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              className="!pl-9"
              aria-label="Search for Files"
              placeholder="Search for Files"
              value={searchQuery}
              onChange={handleSearch}
            />
          </div>

          <div style={{ display: "flex", width: "50%", justifyContent: "end" }}>
            <Uploadfile
              classname="filecogChatBtn"
              refreshchat={filesQuery.refetch}
              compType="data_interpreter"
            />
          </div>
        </div>

        <p style={{ fontWeight: 600, marginBottom: "8px", fontSize: "14px" }}>
          Filter by:
        </p>
        <div style={{ display: "flex", gap: "12px", marginBottom: "20px" }}>
          <FilterDropdown
            value={agentFilter}
            onChange={setAgentFilter}
            placeholder="All agents"
            options={[
              { value: "", label: "All agents" },
              ...Array.from(
                new Set(
                  (userFiles || []).map((f) => f.assistant_name).filter(Boolean)
                )
              ).map((name) => ({ value: name, label: name })),
            ]}
          />
          <FilterDropdown
            value={fileTypeFilter}
            onChange={setFileTypeFilter}
            placeholder="All files"
            options={[
              { value: "", label: "All files" },
              ...Array.from(
                new Set((userFiles || []).map((f) => f.name).filter(Boolean))
              ).map((name) => ({ value: name, label: name })),
            ]}
          />
        </div>
        <TooltipProvider>
          <DataGrid
            columns="auto minmax(0,2fr) minmax(0,1fr) minmax(0,1fr) minmax(0,1fr) auto"
            minWidth={760}
          >
            <DataGridHeader>
              <DataGridCell></DataGridCell>
              <DataGridCell className="text-muted-foreground text-xs uppercase tracking-wide">File Name</DataGridCell>
              <DataGridCell className="text-muted-foreground text-xs uppercase tracking-wide">Agents</DataGridCell>
              <DataGridCell className="text-muted-foreground text-xs uppercase tracking-wide">Created</DataGridCell>
              <DataGridCell className="text-muted-foreground text-xs uppercase tracking-wide">Added By</DataGridCell>
              <DataGridCell className="text-right"></DataGridCell>
            </DataGridHeader>
            {fileLoading ? (
              <div className="text-center p-4">Loading Files...</div>
            ) : (searchQuery ? filteredFiles : filteredData)?.length > 0 ? (
              currentItems.map((item, index) => (
                <DataGridRow key={item.id || index}>
                  <DataGridCell className="text-foreground">
                    <FileText className="h-5 w-5" />
                  </DataGridCell>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <DataGridCell className="text-foreground">
                        {item.name}
                      </DataGridCell>
                    </TooltipTrigger>
                    <TooltipContent side="top" align="start">
                      {item.name}
                    </TooltipContent>
                  </Tooltip>
                  <DataGridCell className="text-foreground">
                    {item.assistant_name?.replace("Your", "Brand")}
                  </DataGridCell>
                  <DataGridCell className="text-foreground">
                    {format(new Date(item?.created_at), "dd/MM/yyyy")}
                  </DataGridCell>
                  <DataGridCell className="text-foreground">
                    {item.username}
                  </DataGridCell>
                  <DataGridCell className="text-right">
                    <div className="flex justify-end items-center">
                      <button
                        type="button"
                        aria-label="Delete file"
                        onClick={() => confirmDelete(item)}
                        className="text-muted-foreground hover:text-destructive cursor-pointer"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                  </DataGridCell>
                </DataGridRow>
              ))
            ) : (
              <div className="text-center p-4">No files found.</div>
            )}
          </DataGrid>
        </TooltipProvider>

        {/* Pagination Controls */}
        {!fileLoading ? (
          <div className="flex justify-center gap-2.5 mt-5">
            <Button
              variant="outline"
              onClick={handlePreviousPage}
              disabled={currentPage === 1}
            >
              Previous
            </Button>

            {[...Array(endPage - startPage + 1)].map((_, index) => {
              const pageNumber = startPage + index;
              return (
                <Button
                  key={pageNumber}
                  variant={pageNumber === currentPage ? "default" : "outline"}
                  onClick={() => handlePageChange(pageNumber)}
                >
                  {pageNumber}
                </Button>
              );
            })}

            <Button
              variant="outline"
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
            >
              Next
            </Button>
          </div>
        ) : (
          ""
        )}
      </>

      {selectPlan !== "" ? (
        <Plans
          justCloseIt={() => setSelectPlan(false)}
          isModalOpen={selectPlan ? true : false}
        />
      ) : (
        ""
      )}

      {seletedFile !== "" ? (
        <DeleteFileModal
          isModalOpen={seletedFile ? true : false}
          yesDelete={deleteFile}
          justCloseIt={() => {
            setSeletedFile(false);
          }}
          fileInfo={seletedFile}
        />
      ) : (
        ""
      )}
    </div>
  );
}

export default DataInterpreter;
